import { useEffect, useState } from "react";
import { Wifi, Network, Bell, Smartphone, ShieldCheck, KeyRound } from "lucide-react";
import { apiGet } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { cn } from "@/lib/utils";

const TAB_ORDER = ["global", "elevate", "regen", "eibattery", "personal"];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function StatusChip({ icon: Icon, label, value, color = "#18c9ff", testId, className }) {
  return (
    <div className={cn("flex items-center gap-1.5", className)} data-testid={testId} title={`${label}: ${value}`}>
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <div className="leading-none">
        <div className="font-ui text-[9px] font-medium uppercase tracking-wider text-jarvis-cyanBright/40">{label}</div>
        <div className="font-num text-[10px] uppercase tracking-wider mt-0.5" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

const TAB_CLIP = { clipPath: "polygon(12px 0, 100% 0, calc(100% - 12px) 100%, 0 100%)" };

export function TopHud({ aiStatus }) {
  const { workspaces, workspace, setWorkspace } = useWorkspace();
  const now = useClock();
  const [h, setH] = useState(null);

  useEffect(() => {
    const load = () => apiGet("/system/health").then(setH).catch(() => {});
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const tabs = TAB_ORDER
    .map((id) => workspaces.find((w) => w.id === id))
    .filter(Boolean);

  return (
    <header className="shrink-0 h-[72px] flex items-center justify-between gap-3 px-3 border-b border-jarvis-cyan/15 bg-jarvis-cyan/[0.02]" data-testid="top-command-bar">
      {/* Workspace tabs (trapezoidal) */}
      <nav className="flex items-end gap-1.5 min-w-0 overflow-x-auto" data-testid="workspace-tabs" aria-label="Workspaces">
        {tabs.map((w) => {
          const active = workspace === w.id;
          return (
            <button
              key={w.id}
              onClick={() => setWorkspace(w.id)}
              data-testid={`workspace-tab-${w.id}`}
              aria-pressed={active}
              style={TAB_CLIP}
              className={cn(
                "font-brand text-[10px] lg:text-[11px] font-semibold uppercase tracking-[0.12em] px-4 lg:px-5 h-[42px] flex items-center whitespace-nowrap border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-jarvis-cyan",
                active
                  ? "text-jarvis-cyanBright border-jarvis-cyan bg-jarvis-blue/20 shadow-[0_6px_16px_-6px_rgba(24,201,255,0.7)]"
                  : "text-jarvis-cyanBright/45 border-jarvis-cyan/25 hover:text-jarvis-cyanBright/80 hover:bg-jarvis-cyan/[0.06]"
              )}
            >
              {w.short}
            </button>
          );
        })}
      </nav>

      {/* Center brand block */}
      <div className="hidden lg:flex items-center gap-3 shrink-0">
        <span className="h-px w-14 bg-gradient-to-r from-transparent to-jarvis-cyan/50" />
        <div className="flex flex-col items-center leading-none">
          <div className="flex items-center gap-2">
            <span className="font-brand text-2xl font-bold tracking-[0.42em] text-jarvis-cyanBright jv-glow pl-2">JARVIS</span>
            {aiStatus?.ai_status === "not_connected" && <span data-testid="hud-ai-not-connected" className="font-num text-[8px] uppercase tracking-wider px-1.5 py-0.5 border border-jarvis-amber/50 text-jarvis-amber">AI not connected</span>}
          </div>
          <span className="font-ui text-[11px] font-medium uppercase tracking-[0.42em] text-jarvis-cyan/70 mt-1">
            {h?.emergency ? "app workers halted" : "private command center"}
          </span>
        </div>
        <span className="h-px w-14 bg-gradient-to-l from-transparent to-jarvis-cyan/50" />
      </div>

      {/* Right status strip */}
      <div className="flex items-center gap-2.5 lg:gap-3 shrink-0">
        <StatusChip icon={ShieldCheck} label="Sentinel" value={h?.sentinel || "—"} color="#18c9ff" testId="hud-sentinel" className="hidden md:flex" />
        <div className="hidden md:block w-px h-7 bg-jarvis-cyan/15" />
        <StatusChip icon={KeyRound} label="Vault" value={h?.vault || "—"} color="#18c9ff" testId="hud-vault" className="hidden md:flex" />
        <div className="hidden md:block w-px h-7 bg-jarvis-cyan/15" />
        <div className="text-right leading-none hidden sm:block px-1" data-testid="hud-clock">
          <div className="font-num text-lg text-jarvis-cyanBright">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
          <div className="font-ui text-[10px] uppercase tracking-wider text-jarvis-cyanBright/40">{now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</div>
        </div>
        <div className="hidden lg:block w-px h-7 bg-jarvis-cyan/15" />
        <StatusChip icon={Wifi} label="Wi-Fi" value="Not connected" color="#55778a" testId="hud-wifi" className="hidden lg:flex" />
        <StatusChip icon={Network} label="Ethernet" value="Not connected" color="#55778a" testId="hud-ethernet" className="hidden lg:flex" />
        <div className="hidden lg:block w-px h-7 bg-jarvis-cyan/15" />
        <StatusChip icon={Bell} label="Alerts" value="Not connected" color="#64748b" testId="hud-notifications" />
        <StatusChip icon={Smartphone} label="Devices" value="Not connected" color="#64748b" testId="hud-devices" />
      </div>
    </header>
  );
}
